import React, { createContext, useContext, useEffect, useReducer } from 'react'
import { TAction, IInitialState, IInitialContextValue } from '../reducers/actionType/actionType'
import reducer from '../reducers/reducer'
import { GlobalActionTypes } from '../actions'
import UseAxios from '../API/axiosInstanceSetup'
import { responseCodes } from '../config'
import { CustomizedSnackbars } from '../components'
import SimpleBackdrop from '../components/backdrop/backdrop'
import { TChildElementProps } from '../types'

// @Gowtham
// to set the initial state for useReducer
const initialState: IInitialState = {
  loading: false,
  alertOpen: false,
  snackbar: {
    message: '',
    type: '',
    status: 0,
  },
}

// @Gowtham
// to set the initial context value
const initialContextValue: IInitialContextValue = {
  initialState,
  customAlertHandler: () => null,
  apiRequest: () => null,
  customSpinnerLoader: () => null,
  dispatchAlertClose: () => null,
}

const CustomContext = createContext(initialContextValue)

export const ContextProvider = ({ children }: TChildElementProps) => {
  const [state, dispatch]: [IInitialState, React.Dispatch<TAction>] = useReducer(
    reducer,
    initialState,
  )

  /*
@Gowtham
@func :
 - that dispatches message, status and type state of the snackbar
 @params : 
    - message => messages to display in the snackbar 
    - type => type should be either "success" or "error" 
    - status => api response status 
*/
  const apiResponse = (message: string, type: string, status: number) => {
    dispatch({
      type: GlobalActionTypes.SNACKBAR_UPDATE,
      payload: {
        message,
        status,
        type,
      },
    })
  }

  /*
@Gowtham
@func :
 - that dispatches appearance state of the snackbar
*/
  const dispatchAlertClose = () => {
    dispatch({
      type: GlobalActionTypes.ALERT_CLOSE,
      payload: false,
    })
  }

  /*
@Gowtham
@func :
 - that dispatches appearance state of the snackbar
*/
  const dispatchAlertOpen = () => {
    dispatchAlertClose()
    dispatch({
      type: GlobalActionTypes.ALERT_OPEN,
      payload: true,
    })
    if (initialState.alertOpen) {
      setTimeout(() => {
        dispatchAlertClose()
      }, 6000)
    }
  }

  /*
@Gowtham
@func :
 - that dispatches appearance state of the spinner-(loader) (closes)
*/
  const dispatchSpinnerClose = () => {
    dispatch({
      type: GlobalActionTypes.LOADING_TO_FALSE,
      payload: false,
    })
  }

  /*
@Gowtham
@func :
 - that dispatches appearance state of the spinner-(loader) (opens)
*/
  const dispatchSpinnerOpen = () => {
    dispatchSpinnerClose()
    dispatch({
      type: GlobalActionTypes.LOADING_TO_TRUE,
      payload: true,
    })
  }

  /*
@Gowtham
@func :
 - that dispatches appearance state of the spinner(loader) -(custom)
*/
  const customSpinnerLoader = (value: boolean) => {
    if (value) dispatchSpinnerOpen()
    else dispatchSpinnerClose()
  }

  /* 
   @Gowtham
   @params :
    - response => String that'll be displayed in the alert
    - status =>
  
  */
  const customAlertHandler = (message: string, type?: 'success' | 'error', status?: number) => {
    const lowerCasing = message.replace(
      /\S*/g,
      (word) => `${word.slice(0, 1)}${word.slice(1).toLowerCase()}`,
    )
    dispatchAlertOpen()
    if (status && type) {
      apiResponse(lowerCasing, type, status)
    } else apiResponse(lowerCasing, 'error', 400)
  }
  type TErrData = {
    error: string
    message: string
    statusCode: number
  }

  /*
@Gowtham
@params :
 - status => this func should only receive api response status as params
*/
  const responseHandler = (errData: TErrData) => {
    try {
      const { statusCode, message } = errData
      const findStatus = responseCodes.find((response) => response.code === statusCode)
      // opens snackbar (alert)
      dispatchAlertOpen()
      if (findStatus) {
        if (findStatus.message === 'Unauthorized' || statusCode === 401) {
          // sets snackbar (alert) messages
          apiResponse('Session Expired, Please Login Again', 'error', 440)

          // closes snackbar (alert)
          dispatchSpinnerOpen()
          setTimeout(() => {
            sessionStorage.clear()
            window.location.reload()
          }, 2500)
        } else {
          apiResponse(message ? message : findStatus.message, findStatus.type, statusCode)
        }
      } else apiResponse('Unknown Error', 'error', statusCode)
    } catch (err) {
      customAlertHandler('Unknown Error, Please try again after sometime', 'error', 400)
    }
  }

  /*
@Gowtham
@params :
 - method => it accepts either 'get' or 'post'
 - url => pass the path params
 - alert => required due to set toast messages on the screen
 - spinner => required due to spinner loading on the screen until the response
              received from the server
 - body => params if necessary related to the method
*/
  const apiRequest = async (
    method: string,
    url: string,
    alert: boolean,
    spinner: boolean,
    body?: any,
  ) => {
    const config = {
      method,
      url,
      data: body,
    }

    if (spinner) {
      dispatchSpinnerOpen()
    } // res.status
    const response = await UseAxios(config)
      .then((res) => {
        if (alert) responseHandler({ error: '', statusCode: 200, message: '' })
        return res.data
      })
      .catch((err) => {
        const { data } = err.response
        console.log('err', err)
        responseHandler(data)
        return Promise.reject(err)
        // return err
      })
      .finally(() => {
        if (spinner) dispatchSpinnerClose()
      })

    return response
  }

  /*
@Gowtham
@context-value :
      context value object that we can access on entire app
*/
  const contextValue: IInitialContextValue = {
    initialState: { ...state },
    apiRequest,
    customAlertHandler,
    customSpinnerLoader,
    dispatchAlertClose,
  }

  return (
    <CustomContext.Provider value={contextValue}>
      {children}
      {/* 
      @Gowtham 
      -Backdrop => spinner renders on conditional api request
      -Snackbar => to set the alert/toast messages according to server response  
      -props => to be given to customize the position of the alert (not required)
    */}
      <SimpleBackdrop loader={false} />
      <CustomizedSnackbars />
    </CustomContext.Provider>
  )
}

// @Gowtham
// to use context
export const useCustomContext = () => {
  return useContext(CustomContext)
}
